<template>
  <div>
    <v-row class="mx-2">
      <v-col cols="12">
        <app-title texto="ATUAÇÃO PROFISSIONAL" />
      </v-col>

      <!-- Gráfico de pizza - cargos e funções -->
      <v-col cols="12">
        <app-title texto="CARGOS / FUNÇÕES" cor="secondary" />
      </v-col>
      <v-col lg="6" md="12" sm="12">
        <pie-chart
          titulo="Comparativo de Cargos"
          :fonte="`Sistema Desenvolva, ${ano}`"
          :dados="dadosPieChartCargos.series"
          :is-get-svg="isGetSvg.cargo"
          @svg="(value) => svg.cargo = value"
        />
      </v-col>
      <v-col lg="6" md="12" sm="12">
        <pie-chart
          titulo="Comparativo de Funções"
          :fonte="`Sistema Desenvolva, ${ano}`"
          :dados="dadosPieChartFuncoes.series"
          :is-get-svg="isGetSvg.funcao"
          @svg="(value) => svg.funcao = value"
        />
      </v-col>

      <!-- Quadro Resumo -->
      <v-col cols="12">
        <app-title texto="QUADRO RESUMO" cor="secondary" />
      </v-col>
      <v-col>
        <tabela-quadro-resumo
          :relatorio="relatorio"
        />
      </v-col>

      <!-- Resultados -->
      <v-col cols="12">
        <app-title texto="RESULTADOS" cor="secondary" />
      </v-col>
      <v-col cols="12">
        <tabela-resultado
          :relatorio="relatorio"
        />
      </v-col>
    </v-row>

    <!-- Relatório PDF -->
    <v-row
      class="justify-end mb-1"
    >
      <v-tooltip
        max-width="400"
        top
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="success"
            @click="getRelatorio()"
            v-on="on"
          >
            <v-icon>get_app</v-icon>
            BAIXAR PDF
          </v-btn>
        </template>
        <span>Clique para baixar o relatório dos dados em formato PDF.</span>
      </v-tooltip>
    </v-row>
  </div>
</template>

<script>
import AppTitle from '@/components/core/AppTitle';
import PieChart from '@/components/chart/PieChart';
import { normalizar } from '@/modules/texto';
import { generatePDF, preparePdf, convertSvg } from '@/modules/relatorioPDF';
import TabelaQuadroResumo from './TabelaQuadroResumoUnidadeProfissional';
import TabelaResultado from './TabelaResultadoUnidadeProfissional';

export default {
  name: 'RelatorioPerfilUnidadeProfissional',

  components: {
    AppTitle,
    PieChart,
    TabelaQuadroResumo,
    TabelaResultado,
  },

  props: {
    relatorio: {
      type: Object,
      default: () => ({}),
    },
    ano: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      headersResumoNiveisEscolaridade: [
        {
          text: 'UNIDADE',
          value: 'sigla',
          sortable: false,
        },
        ...this.relatorio.niveisEscolaridade.map((d) => ({
          text: d.nome.toUpperCase(),
          value: normalizar(d.nome),
          sortable: false,
          align: 'center',
        })),
        {
          text: 'TOTAL DE SERVIDORES DA UNIDADE',
          value: 'servidores',
          width: '10%',
          sortable: false,
          align: 'center',
        },
      ],
      isGetSvg: {
        cargo: false,
        funcao: false,
      },
      svg: {
        cargo: null,
        funcao: null,
      },
    };
  },

  computed: {
    dadosPieChartCargos() {
      return {
        series: [
          {
            name: 'Cargos',
            data: [
              ...this.relatorio.cargos.map((d) => ({
                name: d.nome,
                y: d.quantidade,
              })).sort((a, b) => {
                if (a.y < b.y) { return 1; }
                if (a.y > b.y) { return -1; }
                return 0;
              }),
            ],
          },
        ],
      };
    },
    dadosPieChartFuncoes() {
      return {
        series: [
          {
            name: 'Funções',
            data: [
              ...this.relatorio.funcoes.map((d) => ({
                name: d.nome,
                y: d.quantidade,
              })).sort((a, b) => {
                if (a.y < b.y) { return 1; }
                if (a.y > b.y) { return -1; }
                return 0;
              }),
            ],
          },
        ],
      };
    },
    dadosResumoNiveisEscolaridade() {
      const niveisEscolaridade = {};
      this.relatorio.niveisEscolaridade.forEach((d) => niveisEscolaridade[normalizar(d.nome)] = d.quantidade);

      return {
        nome: this.relatorio.nome,
        sigla: this.relatorio.sigla,
        ...niveisEscolaridade,
        servidores: this.relatorio.quantidadePerfis,
      };
    },
  },

  methods: {
    async getRelatorio() {
      this.isGetSvg.cargo = true;
      this.isGetSvg.funcao = true;
      await preparePdf(async () => {
        const documento = {
          nome: 'relatorio-unidade-atuacao-profissional-perfil',
          cabecalho: {
            titulo: 'Relatório Por Unidade da Atuação Profissional',
            itens: [
              `Unidade: ${this.relatorio.nome} (${this.relatorio.sigla})`,
            ],
          },
          blocos: [
            {
              titulo: 'GRÁFICOS',
              subtitulo: 'CARGO / FUNÇÃO',
              itens: [
                { tipo: 'imagem', valor: await convertSvg(this.svg.cargo) },
                { tipo: 'imagem', valor: await convertSvg(this.svg.funcao) },
              ],
            },
            {
              titulo: 'QUADRO RESUMO',
              subtitulo: 'CARGO / FUNÇÃO',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'UNIDADE',
                    'CARGOS',
                    'FUNÇÕES',
                    'TOTAL DE SERVIDORES',
                  ],
                  linhas: [
                    [
                      `${this.relatorio.nome} (${this.relatorio.sigla})`,
                      this.relatorio.cargos.length,
                      this.relatorio.funcoes.length,
                      this.relatorio.quantidadePerfis,
                    ],
                  ],
                },
              }],
            },
            {
              titulo: 'RESULTADOS',
              subtitulo: 'CARGO / FUNÇÃO',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'SERVIDOR',
                    'CARGO',
                    'FUNÇÃO',
                  ],
                  linhas: this.relatorio.perfis.map((p) => ([
                    p.servidor,
                    p.cargo ? p.cargo.nome : '',
                    p.funcao ? p.funcao.nome : '',
                  ])),
                },
              }],
            },
          ],
        };
        generatePDF(documento);
      });
      this.isGetSvg.cargo = false;
      this.isGetSvg.funcao = false;
    },
  },
};
</script>
